import { useState } from 'react'
import { Card, Button, Table, Badge, Modal, Form, Row, Col, Spinner, Alert, ProgressBar } from 'react-bootstrap'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import { toast } from 'react-toastify'
import { useAuth } from '../../hooks/useAuth'
import { useClientFilter } from '../../hooks/useClientFilter'
import api from '../../services/api'
import { formatDateOnlyEsMX, getCurrentYearMx } from '../../utils/apiDateFormat'
import BudgetForm from './BudgetForm'
import BudgetDetail from './BudgetDetail'
import { formatCurrency } from '../../utils/formatters'

const BudgetsList = () => {
  const { user } = useAuth()
  const { selectedClientId } = useClientFilter()
  const queryClient = useQueryClient()
  const [showForm, setShowForm] = useState(false)
  const [showDetail, setShowDetail] = useState(false)
  const [selectedBudget, setSelectedBudget] = useState(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [filterEstado, setFilterEstado] = useState('all')
  const [filterTipo, setFilterTipo] = useState('all')
  const [filterYear, setFilterYear] = useState('')

  // Obtener lista de presupuestos
  const { data: budgetsData, isLoading, error } = useQuery(
    ['budgets', searchTerm, filterEstado, filterTipo, filterYear, selectedClientId],
    async () => {
      const params = new URLSearchParams()
      if (searchTerm) params.append('search', searchTerm)
      if (filterEstado !== 'all') params.append('estado', filterEstado)
      if (filterTipo !== 'all') params.append('tipo', filterTipo)
      if (filterYear) params.append('año', filterYear)
      
      const response = await api.get(`/financial/budgets/?${params.toString()}`)
      return response.data.results || response.data || []
    },
    { enabled: !!user }
  )

  // Mutación para crear/actualizar presupuesto
  const budgetMutation = useMutation(
    async (budgetData) => {
      if (budgetData.id) {
        return api.put(`/financial/budgets/${budgetData.id}/`, budgetData)
      } else {
        return api.post('/financial/budgets/', budgetData)
      }
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('budgets')
        setShowForm(false)
        setSelectedBudget(null)
        toast.success(selectedBudget ? 'Presupuesto actualizado exitosamente' : 'Presupuesto creado exitosamente')
      },
      onError: (error) => {
        const message = error.response?.data?.error || error.response?.data?.message || 'Error al guardar presupuesto'
        toast.error(message)
      }
    }
  )

  // Mutación para eliminar presupuesto
  const deleteMutation = useMutation(
    async (budgetId) => {
      return api.delete(`/financial/budgets/${budgetId}/`)
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('budgets')
        toast.success('Presupuesto eliminado exitosamente')
      },
      onError: () => {
        toast.error('Error al eliminar presupuesto')
      }
    }
  )

  const handleCreate = () => {
    setSelectedBudget(null)
    setShowForm(true)
  }

  const handleEdit = (budget) => {
    setSelectedBudget(budget)
    setShowForm(true)
  }

  const handleView = (budget) => {
    setSelectedBudget(budget)
    setShowDetail(true)
  }

  const handleDelete = (budget) => {
    if (window.confirm(`¿Estás seguro de eliminar el presupuesto ${budget.nombre}?`)) {
      deleteMutation.mutate(budget.id)
    }
  }

  const handleSubmit = (formData) => {
    budgetMutation.mutate(formData)
  }

  const getEstadoBadge = (estado) => {
    const badges = {
      'borrador': { bg: 'secondary', text: 'Borrador' },
      'en_aprobacion': { bg: 'warning', text: 'En Aprobación' },
      'vigente': { bg: 'success', text: 'Vigente' },
      'cerrado': { bg: 'dark', text: 'Cerrado' }
    }
    const badge = badges[estado] || { bg: 'secondary', text: estado }
    return <Badge bg={badge.bg}>{badge.text}</Badge>
  }

  const getTipoBadge = (tipo) => {
    const badges = {
      'anual': { bg: 'primary', text: 'Anual' },
      'proyecto': { bg: 'info', text: 'Proyecto' },
      'departamento': { bg: 'success', text: 'Departamento' },
      'otro': { bg: 'secondary', text: 'Otro' }
    }
    const badge = badges[tipo] || { bg: 'secondary', text: tipo }
    return <Badge bg={badge.bg}>{badge.text}</Badge>
  }

  const budgets = budgetsData || []
  const currentYear = getCurrentYearMx()
  const years = Array.from({ length: 5 }, (_, i) => currentYear - 2 + i)

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h3>Presupuestos</h3>
        <Button variant="primary" onClick={handleCreate}>
          + Nuevo Presupuesto
        </Button>
      </div>

      {/* Filtros */}
      <Card className="mb-4">
        <Card.Body>
          <Row>
            <Col md={3}>
              <Form.Control
                type="text"
                placeholder="Buscar por nombre, cliente..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </Col>
            <Col md={2}>
              <Form.Select
                value={filterEstado}
                onChange={(e) => setFilterEstado(e.target.value)}
              >
                <option value="all">Todos los estados</option>
                <option value="borrador">Borrador</option>
                <option value="en_aprobacion">En Aprobación</option>
                <option value="vigente">Vigente</option>
                <option value="cerrado">Cerrado</option>
              </Form.Select>
            </Col>
            <Col md={2}>
              <Form.Select
                value={filterTipo}
                onChange={(e) => setFilterTipo(e.target.value)}
              >
                <option value="all">Todos los tipos</option>
                <option value="anual">Anual</option>
                <option value="proyecto">Proyecto</option>
                <option value="departamento">Departamento</option>
                <option value="otro">Otro</option>
              </Form.Select>
            </Col>
            <Col md={2}>
              <Form.Select
                value={filterYear}
                onChange={(e) => setFilterYear(e.target.value)}
              >
                <option value="">Todos los años</option>
                {years.map(year => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </Form.Select>
            </Col>
            <Col md={3} className="text-end">
              <Badge bg="secondary">Total: {budgets.length}</Badge>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Lista de presupuestos */}
      {isLoading ? (
        <div className="text-center py-5">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Cargando...</span>
          </Spinner>
        </div>
      ) : error ? (
        <Alert variant="danger">
          Error al cargar presupuestos. Por favor, intenta nuevamente.
        </Alert>
      ) : budgets.length === 0 ? (
        <Card>
          <Card.Body className="text-center py-5">
            <p className="text-muted">No se encontraron presupuestos</p>
            <Button variant="primary" onClick={handleCreate}>
              Crear primer presupuesto
            </Button>
          </Card.Body>
        </Card>
      ) : (
        <Card>
          <Card.Body>
            <Table responsive hover>
              <thead>
                <tr>
                  <th>Nombre</th>
                  <th>Cliente</th>
                  <th>Tipo</th>
                  <th>Año</th>
                  <th>Periodo</th>
                  <th>Monto Total</th>
                  <th>Ejecutado</th>
                  <th>% Ejecución</th>
                  <th>Estado</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {budgets.map((budget) => {
                  const porcentaje = budget.porcentaje_ejecucion || 0
                  const variant = porcentaje > 100 ? 'danger' : porcentaje > 80 ? 'warning' : 'success'
                  return (
                    <tr key={budget.id}>
                      <td>{budget.nombre}</td>
                      <td>
                        {budget.cliente_name || 
                         (budget.cliente?.razon_social || budget.cliente?.business_name) || 
                         'N/A'}
                      </td>
                      <td>{getTipoBadge(budget.tipo)}</td>
                      <td>{budget.año}</td>
                      <td>
                        {formatDateOnlyEsMX(budget.fecha_inicio, {}, '—')} - {formatDateOnlyEsMX(budget.fecha_fin, {}, '—')}
                      </td>
                      <td>
                        {formatCurrency(budget.monto_total || 0)}
                      </td>
                      <td>
                        {formatCurrency(budget.monto_ejecutado || 0)}
                      </td>
                      <td>
                        <div className="d-flex align-items-center">
                          <ProgressBar
                            variant={variant}
                            now={Math.min(porcentaje, 100)}
                            label={`${porcentaje.toFixed(1)}%`}
                            style={{ width: '100px', marginRight: '10px' }}
                          />
                        </div>
                      </td>
                      <td>{getEstadoBadge(budget.estado)}</td>
                      <td>
                        <div className="btn-group" role="group">
                          <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={() => handleView(budget)}
                          >
                            Ver
                          </Button>
                          <Button
                            variant="outline-secondary"
                            size="sm"
                            onClick={() => handleEdit(budget)}
                          >
                            Editar
                          </Button>
                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() => handleDelete(budget)}
                            disabled={deleteMutation.isLoading}
                          >
                            Eliminar
                          </Button>
                        </div>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </Table>
          </Card.Body>
        </Card>
      )}

      {/* Modal de formulario */}
      <Modal show={showForm} onHide={() => { setShowForm(false); setSelectedBudget(null) }} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{selectedBudget ? 'Editar Presupuesto' : 'Nuevo Presupuesto'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <BudgetForm
            budget={selectedBudget}
            onSubmit={handleSubmit}
            onCancel={() => { setShowForm(false); setSelectedBudget(null) }}
            isLoading={budgetMutation.isLoading}
          />
        </Modal.Body>
      </Modal>

      {/* Modal de detalle */}
      <Modal show={showDetail} onHide={() => { setShowDetail(false); setSelectedBudget(null) }} size="xl" fullscreen="lg-down">
        <Modal.Header closeButton>
          <Modal.Title>Detalle del Presupuesto</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedBudget && (
            <BudgetDetail
              budget={selectedBudget}
              onEdit={() => {
                setShowDetail(false)
                handleEdit(selectedBudget)
              }}
            />
          )}
        </Modal.Body>
      </Modal>
    </div>
  )
}

export default BudgetsList

